import { Component, ChangeDetectionStrategy, signal, inject, effect, HostListener } from '@angular/core';
import { RouterOutlet, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { Navbar } from '../navbar/navbar';
import { Sidemenu } from '../sidemenu/sidemenu';
import { ConfirmHost } from '../../shared/ui/confirm-host';

/**
 * Application shell: the reusable navy sidebar + top bar wrap every routed module
 * via <router-outlet>. The active module key is resolved from the current route's
 * data, and the mobile sidebar drawer closes automatically on navigation.
 */
@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, Navbar, Sidemenu, ConfirmHost],
  templateUrl: './layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Layout {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  protected readonly sidebarOpen = signal(false);
  protected readonly activeKey = signal<string>('');

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this.resolveKey()),
        takeUntilDestroyed(),
      )
      .subscribe((key) => {
        this.activeKey.set(key);
        this.sidebarOpen.set(false);
      });

    // Lock background scroll while the mobile drawer is open.
    effect(() => {
      document.body.classList.toggle('sidebar-locked', this.sidebarOpen());
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
  }

  toggleSidebar(): void {
    this.sidebarOpen.update((open) => !open);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  /** Walk to the deepest activated child route and read its `key` data. */
  private resolveKey(): string {
    let route = this.route.firstChild;
    let key = '';
    while (route) {
      const routeKey = route.snapshot.data['key'];
      if (routeKey) key = routeKey;
      route = route.firstChild;
    }
    return key;
  }
}
